import { Table, Sheet, Box, IconButton, Tooltip } from "@mui/joy";
import { EditIcon, AddIcon, SaveIcon, PersonRemoveIcon } from "../../../styles/StyledMUI";
import DarkTypography from "../../common/DarkTypography";
import DarkInput from "../../common/DarkInput";
import type { User } from "../../../api/auth/Auth.typed";
import { hrSheetSx, hrTableSx } from "../../../styles/globalStyles";
import { userDisplayName } from "../../../utils/userHelper";

export type UserWithBalance = {
  user: Pick<User, "id" | "first_name" | "last_name" | "email">;
  balance?: { Balance?: number; UpdatedAt?: string };
};

export type LeaveBalanceTableProps = {
  usersWithBalances: UserWithBalance[];
  countersEnabled: boolean;
  canEdit: boolean;
  editingUserId: number | null;
  editValue: string;
  updateLoading: boolean;
  onEditValueChange: (value: string) => void;
  onStartEdit: (ub: UserWithBalance) => void;
  onSaveEdit: (ub: UserWithBalance) => void;
  onCancelEdit: () => void;
  onDissociate: (ub: UserWithBalance) => void;
  getBalanceColor: (days: number) => string;
  formatDate: (dateString: string) => string;
};

export default function LeaveBalanceTable({
  usersWithBalances,
  countersEnabled,
  canEdit,
  editingUserId,
  editValue,
  updateLoading,
  onEditValueChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDissociate,
  getBalanceColor,
  formatDate,
}: LeaveBalanceTableProps) {
  return (
    <Sheet sx={hrSheetSx}>
      <Table hoverRow sx={hrTableSx}>
        <thead>
          <tr>
            <th>Utilisateur</th>
            <th>Email</th>
            <th>Solde</th>
            <th>MAJ</th>
            {canEdit && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {usersWithBalances.map((userBalance) => (
            <tr key={userBalance.user.id}>
              <td>
                <DarkTypography level="body-sm" sx={{ fontWeight: "bold" }}>
                  {userDisplayName(userBalance.user) || ""}
                </DarkTypography>
              </td>
              <td>
                <DarkTypography level="body-sm" colorVariant="muted">
                  {userBalance.user.email || ""}
                </DarkTypography>
              </td>
              <td>
                {countersEnabled ? (
                  editingUserId === userBalance.user.id ? (
                    <DarkInput
                      type="number"
                      value={editValue}
                      onChange={(e) => onEditValueChange(e.target.value)}
                      onBlur={() => setTimeout(() => onSaveEdit(userBalance), 100)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onSaveEdit(userBalance);
                        if (e.key === "Escape") onCancelEdit();
                      }}
                      size="sm"
                      autoFocus
                      sx={{ width: 100 }}
                    />
                  ) : (
                    <DarkTypography
                      level="title-md"
                      sx={{
                        color: getBalanceColor(userBalance.balance?.Balance ?? 0),
                        fontWeight: "bold",
                      }}
                    >
                      {userBalance.balance?.Balance ?? 0}
                      {!userBalance.balance && (
                        <DarkTypography
                          component="span"
                          colorVariant="muted"
                          sx={{ fontSize: "0.7rem", ml: 1 }}
                        >
                          (non configuré)
                        </DarkTypography>
                      )}
                    </DarkTypography>
                  )
                ) : (
                  <DarkTypography level="body-sm">—</DarkTypography>
                )}
              </td>
              <td>
                <DarkTypography level="body-sm" colorVariant="secondary">
                  {userBalance.balance ? formatDate(userBalance.balance.UpdatedAt ?? "") : "-"}
                </DarkTypography>
              </td>
              {canEdit && (
                <td>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
                    {countersEnabled &&
                      (editingUserId === userBalance.user.id ? (
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="success"
                          onClick={() => onSaveEdit(userBalance)}
                          loading={updateLoading}
                        >
                          <SaveIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="sm"
                          variant="soft"
                          color="warning"
                          onClick={() => onStartEdit(userBalance)}
                        >
                          {userBalance.balance ? <EditIcon /> : <AddIcon />}
                        </IconButton>
                      ))}
                    <Tooltip title="Dissocier ce salarié du type de congé">
                      <IconButton
                        size="sm"
                        variant="soft"
                        color="danger"
                        onClick={() => onDissociate(userBalance)}
                        disabled={updateLoading}
                      >
                        <PersonRemoveIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
}
